import { Form, Select as ArcoSelect } from '@arco-design/web-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFocusIdx } from '@bay-email/editor';
import { usePageDefaults } from '@extensions/hooks/usePageDefaults';
import { t } from '@bay-email/core';
import { useField } from 'react-final-form';
import { Input } from '../../../components/Form/Input';
import { Help } from '../UI/Help';

type LineHeightUnit = 'ratio' | 'px' | 'percent' | 'normal';

interface ParsedLineHeight {
	amount: string;
	unit: LineHeightUnit;
}

const LINE_HEIGHT_UNIT_OPTIONS: Array<{ value: LineHeightUnit; label: React.ReactNode }> =
	[
		{ value: 'ratio', label: t('Ratio') },
		{ value: 'px', label: 'px' },
		{ value: 'percent', label: '%' },
		{ value: 'normal', label: t('Normal') },
	];

function trimTrailingZeros(value: number) {
	return value
		.toString()
		.replace(/\.0+$/, '')
		.replace(/(\.\d*?)0+$/, '$1');
}

function parseLineHeight(value?: string): ParsedLineHeight {
	const normalizedValue = value?.toString().trim() || '';

	if (!normalizedValue) {
		return { amount: '', unit: 'ratio' };
	}

	if (normalizedValue === 'normal') {
		return { amount: '', unit: 'normal' };
	}

	if (/^-?\d*\.?\d+px$/i.test(normalizedValue)) {
		return { amount: normalizedValue.replace(/px$/i, ''), unit: 'px' };
	}

	if (/^-?\d*\.?\d+%$/.test(normalizedValue)) {
		return { amount: normalizedValue.replace(/%$/, ''), unit: 'percent' };
	}

	return { amount: normalizedValue, unit: 'ratio' };
}

function convertLineHeightAmount(
	amount: string,
	fromUnit: LineHeightUnit,
	toUnit: LineHeightUnit,
	fontSizePx: number,
) {
	if (!amount || fromUnit === toUnit) {
		return amount;
	}

	const numericValue = Number(amount);
	if (Number.isNaN(numericValue)) {
		return amount;
	}

	if (fromUnit === 'ratio' && toUnit === 'percent') {
		return trimTrailingZeros(numericValue * 100);
	}

	if (fromUnit === 'percent' && toUnit === 'ratio') {
		return trimTrailingZeros(numericValue / 100);
	}

	if (fromUnit === 'ratio' && toUnit === 'px') {
		return trimTrailingZeros(numericValue * fontSizePx);
	}

	if (fromUnit === 'px' && toUnit === 'ratio') {
		return trimTrailingZeros(numericValue / fontSizePx);
	}

	if (fromUnit === 'percent' && toUnit === 'px') {
		return trimTrailingZeros((numericValue / 100) * fontSizePx);
	}

	if (fromUnit === 'px' && toUnit === 'percent') {
		return trimTrailingZeros((numericValue / fontSizePx) * 100);
	}

	return amount;
}

function parsePixelValue(value?: string) {
	if (!value) return undefined;
	const normalizedValue = value.toString().trim().toLowerCase();
	const match = normalizedValue.match(/^-?\d*\.?\d+(px)?$/);
	if (!match) return undefined;

	const numericValue = Number(normalizedValue.replace(/px$/, ''));
	if (Number.isNaN(numericValue)) return undefined;

	return numericValue;
}

function enforceLineHeightMinimum(
	amount: string,
	unit: LineHeightUnit,
	fontSizePx: number,
) {
	const normalizedAmount = amount.trim();
	if (!normalizedAmount || unit === 'normal') {
		return { amount: normalizedAmount, adjusted: false };
	}

	const numericValue = Number(normalizedAmount);
	if (Number.isNaN(numericValue)) {
		return { amount: normalizedAmount, adjusted: false };
	}

	const minValue =
		unit === 'px' ? fontSizePx : unit === 'percent' ? 100 : unit === 'ratio' ? 1 : 0;

	if (numericValue >= minValue) {
		return { amount: normalizedAmount, adjusted: false };
	}

	return {
		amount: trimTrailingZeros(minValue),
		adjusted: true,
	};
}

function formatLineHeightValue(amount: string, unit: LineHeightUnit) {
	const normalizedAmount = amount.trim();

	if (unit === 'normal') {
		return 'normal';
	}

	if (!normalizedAmount) {
		return undefined;
	}

	if (unit === 'px') {
		return `${normalizedAmount}px`;
	}

	if (unit === 'percent') {
		return `${normalizedAmount}%`;
	}

	return normalizedAmount;
}

export function LineHeight({ name }: { name?: string }) {
	const { focusIdx } = useFocusIdx();
	const { lineHeight, fontSize: pageDefaultFontSize } = usePageDefaults();
	const fieldName = name || `${focusIdx}.attributes.line-height`;
	const fontSizeFieldName = useMemo(
		() => fieldName.replace(/line-height$/, 'font-size'),
		[fieldName],
	);
	const lastNonNormalValueRef = useRef<ParsedLineHeight | null>(null);
	const guardMessageTimerRef = useRef<number | null>(null);
	const [guardMessage, setGuardMessage] = useState<string | undefined>();
	const { input, meta } = useField<string | undefined>(fieldName, {
		subscription: {
			value: true,
			touched: true,
			error: true,
		},
	});
	const { input: fontSizeInput } = useField<string | undefined>(fontSizeFieldName, {
		subscription: {
			value: true,
		},
	});

	const parsedValue = useMemo(() => parseLineHeight(input.value), [input.value]);
	const parsedDefaultValue = useMemo(() => parseLineHeight(lineHeight), [lineHeight]);
	const activeFontSizePx = useMemo(() => {
		return (
			parsePixelValue(fontSizeInput.value) || parsePixelValue(pageDefaultFontSize) || 14
		);
	}, [fontSizeInput.value, pageDefaultFontSize]);

	const showGuardMessage = useCallback((message: string) => {
		setGuardMessage(message);
		if (guardMessageTimerRef.current) {
			window.clearTimeout(guardMessageTimerRef.current);
		}
		guardMessageTimerRef.current = window.setTimeout(() => {
			setGuardMessage(undefined);
			guardMessageTimerRef.current = null;
		}, 2200);
	}, []);

	useEffect(() => {
		return () => {
			if (guardMessageTimerRef.current) {
				window.clearTimeout(guardMessageTimerRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (parsedValue.unit !== 'normal' && parsedValue.amount) {
			lastNonNormalValueRef.current = {
				amount: parsedValue.amount,
				unit: parsedValue.unit,
			};
		}
	}, [parsedValue.amount, parsedValue.unit]);

	const placeholder = useMemo(() => {
		if (parsedValue.unit === 'normal') {
			return '';
		}

		if (parsedDefaultValue.unit === parsedValue.unit && parsedDefaultValue.amount) {
			return parsedDefaultValue.amount;
		}

		if (parsedValue.unit === 'ratio') {
			return (
				convertLineHeightAmount(
					parsedDefaultValue.amount || '1.7',
					parsedDefaultValue.unit,
					'ratio',
					activeFontSizePx,
				) || '1.7'
			);
		}

		if (parsedValue.unit === 'percent') {
			return (
				convertLineHeightAmount(
					parsedDefaultValue.amount || '1.7',
					parsedDefaultValue.unit,
					'percent',
					activeFontSizePx,
				) || '170'
			);
		}

		return parsedDefaultValue.unit === 'px' ? parsedDefaultValue.amount : '';
	}, [
		activeFontSizePx,
		parsedDefaultValue.amount,
		parsedDefaultValue.unit,
		parsedValue.unit,
	]);

	const onAmountChange = useCallback(
		(nextAmount: string) => {
			const enforcedResult = enforceLineHeightMinimum(
				nextAmount,
				parsedValue.unit,
				activeFontSizePx,
			);

			if (enforcedResult.adjusted) {
				const minimumLabel =
					parsedValue.unit === 'px'
						? `${trimTrailingZeros(activeFontSizePx)}px`
						: parsedValue.unit === 'percent'
							? '100%'
							: '1';
				showGuardMessage(
					t(`Line height adjusted to minimum ${minimumLabel} to avoid collapsing text.`),
				);
			}

			if (parsedValue.unit !== 'normal' && nextAmount.trim()) {
				lastNonNormalValueRef.current = {
					amount: enforcedResult.amount,
					unit: parsedValue.unit,
				};
			}
			input.onChange(formatLineHeightValue(enforcedResult.amount, parsedValue.unit));
		},
		[activeFontSizePx, input, parsedValue.unit, showGuardMessage],
	);

	const onUnitChange = useCallback(
		(nextUnit: string) => {
			const lineHeightUnit = nextUnit as LineHeightUnit;

			if (lineHeightUnit === 'normal') {
				if (parsedValue.unit !== 'normal' && parsedValue.amount) {
					lastNonNormalValueRef.current = {
						amount: parsedValue.amount,
						unit: parsedValue.unit,
					};
				}
				input.onChange('normal');
				return;
			}

			if (parsedValue.unit === 'normal') {
				const lastNonNormalValue = lastNonNormalValueRef.current;
				if (lastNonNormalValue?.amount) {
					const restoredAmount = convertLineHeightAmount(
						lastNonNormalValue.amount,
						lastNonNormalValue.unit,
						lineHeightUnit,
						activeFontSizePx,
					);
					const enforcedRestoredAmount = enforceLineHeightMinimum(
						restoredAmount,
						lineHeightUnit,
						activeFontSizePx,
					).amount;
					input.onChange(formatLineHeightValue(enforcedRestoredAmount, lineHeightUnit));
					return;
				}

				const fallbackAmount =
					convertLineHeightAmount(
						parsedDefaultValue.amount || '1.7',
						parsedDefaultValue.unit,
						lineHeightUnit,
						activeFontSizePx,
					) || (lineHeightUnit === 'percent' ? '170' : '1.7');
				const enforcedFallbackAmount = enforceLineHeightMinimum(
					fallbackAmount,
					lineHeightUnit,
					activeFontSizePx,
				).amount;
				input.onChange(formatLineHeightValue(enforcedFallbackAmount, lineHeightUnit));
				return;
			}

			const convertedAmount = convertLineHeightAmount(
				parsedValue.amount,
				parsedValue.unit,
				lineHeightUnit,
				activeFontSizePx,
			);

			const enforcedConvertedAmount = enforceLineHeightMinimum(
				convertedAmount,
				lineHeightUnit,
				activeFontSizePx,
			).amount;

			input.onChange(formatLineHeightValue(enforcedConvertedAmount, lineHeightUnit));
		},
		[
			activeFontSizePx,
			input,
			parsedDefaultValue.amount,
			parsedDefaultValue.unit,
			parsedValue.amount,
			parsedValue.unit,
		],
	);

	return (
		<Form.Item
			style={{
				marginTop: '0px',
				marginLeft: '0px',
				marginRight: '0px',
				marginBottom: '12px',
			}}
			label={
				<span
					style={{
						display: 'inline-flex',
						alignItems: 'center',
						gap: 6,
						fontWeight: 600,
						color: '#000000',
					}}
				>
					<span style={{ textTransform: 'capitalize' }}>{t('Line height')}</span>
					<Help
						title={t(
							'Use Ratio for values like 1.7. px and % are also supported, and you can choose Normal from the suffix selector.',
						)}
						placement='top-end'
						overlayClassName='line-height-help-tooltip'
						style={{ color: '#6b7280' }}
					/>
				</span>
			}
			labelAlign='left'
			labelCol={{
				span: 24,
				style: {
					paddingRight: 0,
					paddingLeft: 0,
				},
			}}
			wrapperCol={{
				span: 24,
				style: {
					paddingLeft: 0,
				},
			}}
			validateStatus={meta.touched && meta.error ? 'error' : undefined}
			help={meta.touched && meta.error ? meta.error : guardMessage}
		>
			<Input
				className='line-height-input-with-unit'
				autoComplete='off'
				inputMode='decimal'
				quickchange
				value={parsedValue.unit === 'normal' ? '' : parsedValue.amount}
				placeholder={placeholder}
				disabled={parsedValue.unit === 'normal'}
				onChange={onAmountChange}
				onBlur={input.onBlur}
				aria-label={t('Line height value')}
				addAfter={
					<ArcoSelect
						value={parsedValue.unit}
						onChange={value => onUnitChange(String(value))}
						onBlur={input.onBlur}
						showSearch={false}
						aria-label={t('Line height unit')}
						getPopupContainer={() => document.body}
						triggerProps={{
							position: 'bl',
							autoFitPosition: true,
							autoFitTransformOrigin: true,
							updateOnScroll: true,
						}}
						style={{
							width: 92,
							height: '100%',
						}}
					>
						{LINE_HEIGHT_UNIT_OPTIONS.map(option => (
							<ArcoSelect.Option
								key={option.value}
								value={option.value}
							>
								{typeof option.label === 'string' ? option.label : option.value}
							</ArcoSelect.Option>
						))}
					</ArcoSelect>
				}
			/>
		</Form.Item>
	);
}
